/**
 * Notification context (V3). Shared by brand + admin app roots.
 *
 * Uses apiClient directly (not @discovr/api — that would be a circular package
 * dependency, since @discovr/api imports apiClient from here). Read model is
 * shared/team: opening is collective, mark-all-read is per-user, delete is a
 * per-user dismiss. SSE `new_notification` nudges a refetch; the DB is the
 * source of truth.
 *
 * Two tabs, two independent paginated lists: Messages (chat, filterable by
 * thread_type) and Activities (status/event feed). Each tab lazy-loads the
 * first time it becomes active; Messages loads on mount since it's the
 * default landing tab.
 */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AppNotification, DataResponse } from '@discovr/types';
import { apiClient } from '../api';
import { useAuth, invalidateScopesAndProfile } from '../auth';
import { useNotificationsSSE, type NewNotificationPayload } from '../sse/useNotificationsSSE';

export type NotificationTab = 'messages' | 'activities';

interface NotificationPage {
  items: AppNotification[];
  next_cursor: string;
}

interface UnreadCounts {
  messages: number;
  activities: number;
}

interface TabState {
  items: AppNotification[];
  cursor: string;
  hasMore: boolean;
}

const emptyTabState: TabState = { items: [], cursor: '', hasMore: false };
const PAGE_SIZE = 20;

interface NotificationContextValue {
  activeTab: NotificationTab;
  setActiveTab: (tab: NotificationTab) => void;
  threadType: string;
  setThreadType: (threadType: string) => void;
  messages: AppNotification[];
  activities: AppNotification[];
  hasMoreMessages: boolean;
  hasMoreActivities: boolean;
  unreadCounts: UnreadCounts;
  loading: boolean;
  hasNewNotification: boolean;
  loadMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  deleteNotif: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNewNotificationFlag: () => void;
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  registerToastHandler: (handler: (event: NewNotificationPayload) => void) => () => void;
}

const defaultValue: NotificationContextValue = {
  activeTab: 'messages',
  setActiveTab: () => {},
  threadType: 'selection',
  setThreadType: () => {},
  messages: [],
  activities: [],
  hasMoreMessages: false,
  hasMoreActivities: false,
  unreadCounts: { messages: 0, activities: 0 },
  loading: false,
  hasNewNotification: false,
  loadMore: () => Promise.resolve(),
  markAsRead: () => Promise.resolve(),
  deleteNotif: () => Promise.resolve(),
  markAllAsRead: () => Promise.resolve(),
  clearNewNotificationFlag: () => {},
  isPanelOpen: false,
  setPanelOpen: () => {},
  registerToastHandler: () => () => {},
};

const NotificationContext = React.createContext<NotificationContextValue>(defaultValue);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const enabled = !!user;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = React.useState<NotificationTab>('messages');
  // Negotiation is the default-selected pill on the Messages tab.
  const [threadType, setThreadType] = React.useState('selection');
  const [tabState, setTabState] = React.useState<Record<NotificationTab, TabState>>({
    messages: emptyTabState,
    activities: emptyTabState,
  });
  const [loadedTabs, setLoadedTabs] = React.useState<Set<NotificationTab>>(new Set());
  const [unreadCounts, setUnreadCounts] = React.useState<UnreadCounts>({ messages: 0, activities: 0 });
  const [loading, setLoading] = React.useState(false);
  const [hasNew, setHasNew] = React.useState(false);
  const [isPanelOpen, setPanelOpen] = React.useState(false);

  // Mirrors isPanelOpen so the SSE handler (captured once by useNotificationsSSE's
  // internal ref, but still closing over render-time values via onNewNotification)
  // always reads the latest open/closed state rather than a stale one.
  const isPanelOpenRef = React.useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;

  // Toast handlers registered by UI (e.g. a toast-rendering component) that
  // want to be notified of enriched SSE payloads while the panel is closed.
  const toastHandlersRef = React.useRef(new Set<(event: NewNotificationPayload) => void>());
  const registerToastHandler = React.useCallback((handler: (event: NewNotificationPayload) => void) => {
    toastHandlersRef.current.add(handler);
    return () => {
      toastHandlersRef.current.delete(handler);
    };
  }, []);

  // Guards fetchTab/loadMore against the classic double-click race: if a
  // newer request for a tab starts before an older one resolves (rapid
  // thread-type pill switching, or mashing "Load more"), only the response
  // belonging to the LATEST request for that tab gets applied — otherwise
  // whichever response happens to land last wins regardless of which request
  // was actually issued last, and the UI can show one pill as "selected"
  // while actually displaying a different pill's data. Shared between
  // fetchTab and loadMore (keyed by tab) so a fresh fetchTab reload also
  // correctly supersedes an in-flight loadMore for the same tab.
  const fetchSeqRef = React.useRef<Record<NotificationTab, number>>({ messages: 0, activities: 0 });

  const fetchCounts = React.useCallback(async () => {
    if (!enabled) return;
    try {
      const counts = await apiClient
        .get<DataResponse<UnreadCounts>>('/notifications/unread-count')
        .then((r) => r.data.data);
      setUnreadCounts(counts);
    } catch (err) {
      // best-effort; keep prior counts on transient failures, but log so a
      // real outage/auth-race/rate-limit isn't completely invisible in
      // Sentry (console.error is forwarded via consoleLoggingIntegration).
      console.error('[Notifications] fetchCounts failed', err);
    }
  }, [enabled]);

  const fetchTab = React.useCallback(
    async (tab: NotificationTab) => {
      if (!enabled) return;
      const seq = ++fetchSeqRef.current[tab];
      setLoading(true);
      try {
        const q = new URLSearchParams({ tab, limit: String(PAGE_SIZE) });
        if (tab === 'messages' && threadType) q.set('thread_type', threadType);
        const page = await apiClient
          .get<DataResponse<NotificationPage>>(`/notifications?${q.toString()}`)
          .then((r) => r.data.data);
        if (fetchSeqRef.current[tab] !== seq) return; // a newer request for this tab already won
        setTabState((prev) => ({
          ...prev,
          [tab]: { items: page.items ?? [], cursor: page.next_cursor ?? '', hasMore: !!page.next_cursor },
        }));
        setLoadedTabs((prev) => new Set(prev).add(tab));
      } catch (err) {
        // best-effort; keep prior state on transient failures, but log (see
        // fetchCounts above for why).
        console.error('[Notifications] fetchTab failed', { tab, threadType, err });
      } finally {
        if (fetchSeqRef.current[tab] === seq) setLoading(false);
      }
    },
    [enabled, threadType],
  );

  // Loads whichever tab needs fresh data: either it's never been loaded yet
  // (initial mount / first activation of a tab) or, for Messages, its
  // thread-type filter just changed. Deliberately one effect, not two — a
  // "reload Messages on threadType change" effect and a "lazy-load on first
  // activation" effect used to be separate, and both fired fetchTab('messages')
  // on the same render whenever `enabled` flipped true (auth resolving) while
  // Messages was the active tab, causing two redundant concurrent GETs.
  const prevThreadTypeRef = React.useRef(threadType);
  React.useEffect(() => {
    if (!enabled) return;
    const threadTypeChanged = activeTab === 'messages' && prevThreadTypeRef.current !== threadType;
    prevThreadTypeRef.current = threadType;
    if (threadTypeChanged || !loadedTabs.has(activeTab)) void fetchTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, threadType, enabled]);

  React.useEffect(() => {
    void fetchCounts();
  }, [fetchCounts]);

  useNotificationsSSE({
    enabled,
    onNewNotification: (payload) => {
      setHasNew(true);
      void fetchCounts();
      if (payload.action === 'member.role_changed') {
        invalidateScopesAndProfile(queryClient, user?.uid);
      }
      // Fan out to registered toast handlers only for enriched (category-bearing)
      // payloads and only while the panel is closed — an open panel already shows
      // the new item, so a toast on top of it would be redundant.
      if (payload.category && !isPanelOpenRef.current) {
        toastHandlersRef.current.forEach((handler) => {
          try {
            handler(payload);
          } catch (err) {
            // One bad handler must neither skip the rest of the fan-out nor
            // propagate into the SSE dispatch (which would abort delivery of
            // this event to fetchCounts/loadedTabs bookkeeping below it).
            console.error('[Notifications] toast handler failed', err);
          }
        });
      }
      // The tab that's NOT currently visible might have gone stale too (the
      // new item could belong to it), but we don't refetch it now — just mark
      // it as no-longer-loaded so the existing "lazy-load a tab on
      // activation" effect naturally refetches it the next time the user
      // switches to it. Without this, loadedTabs only ever grows, so
      // switching away, an SSE nudge landing on the other tab, then switching
      // back showed indefinitely stale data (only a full page reload fixed it).
      const inactiveTab: NotificationTab = activeTab === 'messages' ? 'activities' : 'messages';
      setLoadedTabs((prev) => {
        if (!prev.has(inactiveTab)) return prev;
        const next = new Set(prev);
        next.delete(inactiveTab);
        return next;
      });
      // Only safe to refetch-from-scratch when the user hasn't paged past
      // page 1 — fetchTab replaces tabState[tab] with a fresh first page,
      // which would silently collapse any pages loaded via "Load more" (the
      // same bug markAsRead/markAllAsRead were fixed for above), and this
      // handler fires for *any* team member's action, not just the current
      // user's. We can't safely local-patch here like markAsRead does
      // because an SSE nudge doesn't tell us the shape of what changed (a
      // genuinely new item vs. a read-state flip) — so when pages are
      // already loaded, just refresh the badge/pulse and leave the list for
      // the next natural refresh (tab switch, mark-read, etc).
      if (tabState[activeTab].items.length <= PAGE_SIZE) void fetchTab(activeTab);
    },
  });

  const loadMore = React.useCallback(async () => {
    const tab = activeTab;
    const state = tabState[tab];
    if (!enabled || !state.hasMore) return;
    const seq = ++fetchSeqRef.current[tab];
    setLoading(true);
    try {
      const q = new URLSearchParams({ tab, limit: String(PAGE_SIZE), cursor: state.cursor });
      if (tab === 'messages' && threadType) q.set('thread_type', threadType);
      const page = await apiClient
        .get<DataResponse<NotificationPage>>(`/notifications?${q.toString()}`)
        .then((r) => r.data.data);
      if (fetchSeqRef.current[tab] !== seq) return; // a newer request for this tab already won
      setTabState((prev) => ({
        ...prev,
        [tab]: {
          items: [...prev[tab].items, ...(page.items ?? [])],
          cursor: page.next_cursor ?? '',
          hasMore: !!page.next_cursor,
        },
      }));
    } catch (err) {
      // best-effort; log (see fetchCounts above for why).
      console.error('[Notifications] loadMore failed', { tab, threadType, err });
    } finally {
      if (fetchSeqRef.current[tab] === seq) setLoading(false);
    }
  }, [enabled, activeTab, threadType, tabState]);

  // Patches the item's `read` flag in place rather than refetching the tab —
  // fetchTab replaces tabState[tab] with a fresh first page, which would
  // silently collapse any pages the user loaded via "Load more" (and, since
  // the reply input's onFocus triggers markAsRead, even just focusing a
  // reply box on an unread message would snap the list back to 20 rows).
  const markAsRead = React.useCallback(
    async (id: string) => {
      setTabState((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          items: prev[activeTab].items.map((n) => (n.id === id ? { ...n, read: true } : n)),
        },
      }));
      try {
        await apiClient.put(`/notifications/${encodeURIComponent(id)}/read`);
      } catch (err) {
        // Self-heal the optimistic patch: a fresh first page restores server
        // truth (loses "Load more" progress only on this failure path).
        console.error('[Notifications] mark-read failed', id, err);
        void fetchTab(activeTab);
      }
      await fetchCounts();
    },
    [activeTab, fetchCounts, fetchTab],
  );

  const deleteNotif = React.useCallback(
    async (id: string) => {
      setTabState((prev) => ({
        ...prev,
        [activeTab]: { ...prev[activeTab], items: prev[activeTab].items.filter((n) => n.id !== id) },
      }));
      try {
        await apiClient.delete(`/notifications/${encodeURIComponent(id)}`);
      } catch (err) {
        // Self-heal: without this the row silently vanishes now and
        // resurrects on the next unrelated refetch.
        console.error('[Notifications] delete failed', id, err);
        void fetchTab(activeTab);
      }
      await fetchCounts();
    },
    [activeTab, fetchCounts, fetchTab],
  );

  // Same rationale as markAsRead: patch both tabs' loaded pages locally
  // instead of refetching, so "Load more" progress survives a mark-all.
  const markAllAsRead = React.useCallback(async () => {
    setTabState((prev) => ({
      messages: { ...prev.messages, items: prev.messages.items.map((n) => ({ ...n, read: true })) },
      activities: { ...prev.activities, items: prev.activities.items.map((n) => ({ ...n, read: true })) },
    }));
    await apiClient.put('/notifications/read-all');
    await fetchCounts();
  }, [fetchCounts]);

  const clearNewNotificationFlag = React.useCallback(() => setHasNew(false), []);

  const value = React.useMemo<NotificationContextValue>(
    () => ({
      activeTab,
      setActiveTab,
      threadType,
      setThreadType,
      messages: tabState.messages.items,
      activities: tabState.activities.items,
      hasMoreMessages: tabState.messages.hasMore,
      hasMoreActivities: tabState.activities.hasMore,
      unreadCounts,
      loading,
      hasNewNotification: hasNew,
      loadMore,
      markAsRead,
      deleteNotif,
      markAllAsRead,
      clearNewNotificationFlag,
      isPanelOpen,
      setPanelOpen,
      registerToastHandler,
    }),
    [
      activeTab,
      threadType,
      tabState,
      unreadCounts,
      loading,
      hasNew,
      loadMore,
      markAsRead,
      deleteNotif,
      markAllAsRead,
      clearNewNotificationFlag,
      isPanelOpen,
      registerToastHandler,
    ],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  return React.useContext(NotificationContext);
}
